// Sprint 0081 — HR Enablement API client.
import apiClient from '@/shared/api/client'

// ── Employee import ────────────────────────────────────────────────────────

export interface ImportRowResult {
  row_no: number
  status: 'OK' | 'UPDATE' | 'ERROR'
  errors: string[]
  data: Record<string, unknown>
}

export interface ImportPreview {
  total: number
  ok: number
  update: number
  error: number
  rows: ImportRowResult[]
}

export interface ImportCommitResult {
  created: number
  updated: number
  skipped: number
  total: number
}

export const hrImportApi = {
  previewEmployees: async (file: File, plantId: string): Promise<ImportPreview> => {
    const fd = new FormData()
    fd.append('file', file)
    const r = await apiClient.post<ImportPreview>(
      `/hr/employees/import/preview?plant_id=${plantId}`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return r.data
  },

  commitEmployees: async (file: File, plantId: string): Promise<ImportCommitResult> => {
    const fd = new FormData()
    fd.append('file', file)
    const r = await apiClient.post<ImportCommitResult>(
      `/hr/employees/import/commit?plant_id=${plantId}`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return r.data
  },

  previewSkills: async (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    const r = await apiClient.post<ImportPreview>(
      '/hr/skills/import/preview',
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return r.data
  },

  commitSkills: async (file: File) => {
    const fd = new FormData()
    fd.append('file', file)
    const r = await apiClient.post<ImportCommitResult>(
      '/hr/skills/import/commit',
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    )
    return r.data
  },
}

// ── Create user from employee ──────────────────────────────────────────────

export interface CreateUserResult {
  user_id: string
  username: string | null
  temp_password: string | null
  role_assigned: string | null
  was_existing: boolean
}

export interface UnlinkResult {
  employee_id: string
  was_linked: boolean
  user_id_removed: string | null
  user_deactivated: boolean
}

export interface SetAccessResult {
  changed: boolean
  user_id: string
  is_active?: boolean
  is_locked?: boolean
}

export interface ResetPasswordResult {
  user_id: string
  username: string
  temp_password: string
}

export const employeeUserApi = {
  createUserForEmployee: async (employeeId: string): Promise<CreateUserResult> => {
    const r = await apiClient.post<CreateUserResult>(
      `/hr/employees/${employeeId}/create-user`,
      {},
    )
    return r.data
  },

  // Break the employee↔user link. Default also deactivates the user
  // account (set also_deactivate_user=false to leave it usable for
  // re-linking to a different employee).
  unlinkUser: async (
    employeeId: string,
    body: { also_deactivate_user?: boolean; reason?: string } = {},
  ): Promise<UnlinkResult> => {
    const r = await apiClient.post<UnlinkResult>(
      `/hr/employees/${employeeId}/unlink-user`,
      { also_deactivate_user: true, ...body },
    )
    return r.data
  },

  // Toggle is_active / is_locked on the linked user without breaking the link.
  setUserAccess: async (
    employeeId: string,
    body: { is_active?: boolean | null; is_locked?: boolean | null; reason?: string },
  ): Promise<SetAccessResult> => {
    const r = await apiClient.post<SetAccessResult>(
      `/hr/employees/${employeeId}/set-user-access`,
      body,
    )
    return r.data
  },

  // Generate a new temp password. Clears is_locked + sets is_active=true.
  resetPassword: async (employeeId: string): Promise<ResetPasswordResult> => {
    const r = await apiClient.post<ResetPasswordResult>(
      `/hr/employees/${employeeId}/reset-password`,
      {},
    )
    return r.data
  },
}

// ── Dept-filtered user picker ──────────────────────────────────────────────

export interface PickerUser {
  id: string
  username: string
  full_name: string
  email: string
  department_id: string | null
  department_name: string | null
  designation: string | null
  is_active: boolean
}

export const hrUserPickerApi = {
  list: async (params?: { department_id?: string; show_all?: boolean }) => {
    const r = await apiClient.get<PickerUser[]>('/hr/users', { params })
    return r.data
  },
}

// ── Designations ───────────────────────────────────────────────────────────

export interface Designation {
  id: string
  designation_code: string
  designation_name: string
  category: string | null
  is_active: boolean
}

export const designationsApi = {
  list: async (): Promise<Designation[]> => {
    const r = await apiClient.get<Designation[]>('/hr/designations')
    return r.data
  },
  create: async (body: { designation_code: string; designation_name: string; category?: string }) => {
    const r = await apiClient.post<Designation>('/hr/designations', body)
    return r.data
  },
}

export interface SupervisorCandidate {
  id: string
  emp_code: string
  emp_name: string
  designation: string | null
}
export interface ReportingCandidatesResult {
  parent_designation_code: string | null
  parent_designation_name: string | null
  /** Ancestor role actually matched (may be higher than the immediate
   *  parent when the parent role is vacant). Null on fallback. */
  matched_designation_name?: string | null
  filtered: boolean
  candidates: SupervisorCandidate[]
}

// Reporting-supervisor candidates per the org chart (people holding the
// parent designation of the given designation).
export const reportingCandidatesApi = {
  get: async (params: {
    plant_id: string
    designation_code?: string
    exclude_employee_id?: string
  }): Promise<ReportingCandidatesResult> => {
    const r = await apiClient.get<ReportingCandidatesResult>(
      '/hr/org/reporting-supervisor-candidates', { params })
    return r.data
  },
}

// ── Skills ─────────────────────────────────────────────────────────────────

export type TradeType =
  | 'MECHANICAL' | 'ELECTRICAL' | 'ELECTRONIC' | 'UTILITY'
  | 'PRODUCTION' | 'QUALITY' | 'STORE' | 'EHS'

export type SkillCategory =
  | 'PRODUCTION_OPERATION' | 'MAINTENANCE' | 'ELECTRICAL' | 'ELECTRONIC'
  | 'UTILITY' | 'STORE' | 'QC' | 'SAFETY' | 'SUPERVISORY'

export type CertStatus =
  | 'NOT_CERTIFIED' | 'TRAINING' | 'CERTIFIED' | 'EXPIRED' | 'SUSPENDED'

export interface Skill {
  id: string
  employee_id: string
  department_id: string | null
  skill_category: SkillCategory
  skill_name: string
  trade_type: TradeType
  machine_type: string | null
  machine_id: string | null
  skill_level: number
  certification_status: CertStatus
  certified_from: string | null
  certified_till: string | null
  remarks: string | null
  active: boolean
  created_at: string
  updated_at: string
}

export interface SkillCreateBody {
  employee_id: string
  skill_category: SkillCategory
  skill_name: string
  trade_type: TradeType
  skill_level?: number
  certification_status?: CertStatus
  machine_type?: string | null
  machine_id?: string | null
  department_id?: string | null
  certified_from?: string | null
  certified_till?: string | null
  remarks?: string | null
}

export interface SkillUpdateBody {
  skill_level?: number
  certification_status?: CertStatus
  certified_from?: string | null
  certified_till?: string | null
  remarks?: string | null
  active?: boolean
}

export const skillsApi = {
  list: async (params: {
    department_id?: string
    trade_type?: TradeType
    skill_category?: SkillCategory
    skill_level_min?: number
    certification_status?: CertStatus
    machine_type?: string
    machine_id?: string
    include_inactive?: boolean
    limit?: number
  } = {}): Promise<Skill[]> => {
    const r = await apiClient.get<Skill[]>('/hr/skills', { params })
    return r.data
  },

  listForEmployee: async (employeeId: string): Promise<Skill[]> => {
    const r = await apiClient.get<Skill[]>(`/hr/employees/${employeeId}/skills`)
    return r.data
  },

  create: async (body: SkillCreateBody) => {
    const r = await apiClient.post<Skill>('/hr/skills', body)
    return r.data
  },

  update: async (id: string, body: SkillUpdateBody) => {
    const r = await apiClient.patch<Skill>(`/hr/skills/${id}`, body)
    return r.data
  },

  certifiedWorkers: async (params: {
    trade_type?: TradeType
    machine_type?: string
    department_id?: string
  } = {}) => {
    const r = await apiClient.get<Array<{
      employee_id: string
      emp_code: string
      emp_name: string
      user_id: string | null
      trade_type: TradeType
      machine_type: string | null
      skill_level: number
      certified_till: string | null
    }>>('/hr/skills/certified-workers', { params })
    return r.data
  },
}

// Constants for UI dropdowns
export const TRADE_TYPES: TradeType[] = [
  'MECHANICAL', 'ELECTRICAL', 'ELECTRONIC', 'UTILITY',
  'PRODUCTION', 'QUALITY', 'STORE', 'EHS',
]

export const SKILL_CATEGORIES: SkillCategory[] = [
  'PRODUCTION_OPERATION', 'MAINTENANCE', 'ELECTRICAL', 'ELECTRONIC',
  'UTILITY', 'STORE', 'QC', 'SAFETY', 'SUPERVISORY',
]

export const CERT_STATUSES: CertStatus[] = [
  'NOT_CERTIFIED', 'TRAINING', 'CERTIFIED', 'EXPIRED', 'SUSPENDED',
]

export const SKILL_LEVEL_LABELS: Record<number, string> = {
  0: '0 — Untrained',
  1: '1 — Beginner',
  2: '2 — Trained',
  3: '3 — Certified',
  4: '4 — Multi-skilled',
  5: '5 — Trainer / Mentor',
}
